import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  TrendingUp, BookOpen, Briefcase, Palette, Cpu,
  Sparkles, Users, ArrowRight, Loader2, ShieldCheck, X
} from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { TrendingUp, BookOpen, Briefcase, Palette, Cpu };

/**
 * Deep Research entry modal: pick domain → describe task → pay ₺249 → job starts.
 * If admin, skip payment.
 */
export const DeepResearchModal = ({ open, onOpenChange, user, onJobStarted, onShowLeaderboard, onShowCustomAgents }) => {
  const { t, currentLang } = useLanguage();
  const [step, setStep] = useState("intro"); // intro | domain | task | paying | starting
  const [domain, setDomain] = useState(null);
  const [task, setTask] = useState("");
  const [domains, setDomains] = useState([]);
  const [agentCount, setAgentCount] = useState(0);
  const [price, setPrice] = useState(249);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [d, a] = await Promise.all([
          axios.get(`${API}/deep-research/domains`),
          axios.get(`${API}/deep-research/agents`),
        ]);
        setDomains(d.data.domains || []);
        setPrice(d.data.price || 249);
        setAgentCount(a.data.total || 0);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep("intro");
        setDomain(null);
        setTask("");
      }, 300);
    }
  }, [open]);

  const isAdmin = user?.is_admin;

  const startAdminJob = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/deep-research/start`, {
        task,
        domain: domain.id,
        language: currentLang,
      });
      toast.success(t("dr_job_started"));
      onOpenChange(false);
      onJobStarted?.(res.data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dr_error"));
    } finally {
      setLoading(false);
    }
  };

  const startPurchase = async () => {
    setLoading(true);
    try {
      // Store task in localStorage so we can resume after Stripe redirect
      const draft = { task, domain: domain.id, language: currentLang, ts: Date.now() };
      localStorage.setItem("dr_pending_task", JSON.stringify(draft));
      const res = await axios.post(`${API}/deep-research/purchase`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dr_payment_failed"));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="deep-research-modal"
        className="max-w-2xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/30 border-purple-500/30 p-0 overflow-hidden"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"
          data-testid="dr-close-btn"
        >
          <X size={16} />
        </button>

        {step === "intro" && (
          <div className="p-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/40">
                <Sparkles size={32} className="text-white" />
              </div>
            </div>
            <DialogHeader>
              <DialogTitle className="text-center text-2xl text-white">
                {t("dr_title")}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-400 text-center mt-2 mb-6 leading-relaxed">
              {t("dr_intro_desc")}
            </p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <Stat icon={Users} label={t("dr_agents_in_pool")} value={`${agentCount}+`} />
              <Stat icon={Sparkles} label={t("dr_phases")} value="10" />
              <Stat icon={ShieldCheck} label={t("dr_delivery")} value={t("dr_delivery_value")} />
            </div>

            <div className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-4 mb-5 text-sm text-zinc-300 leading-relaxed">
              {t("dr_pricing_note").replace("{price}", `₺${price}`)}
            </div>

            <Button
              data-testid="dr-start-btn"
              onClick={() => setStep("domain")}
              className="w-full h-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold"
            >
              {t("dr_get_started")} <ArrowRight size={16} className="ml-1" />
            </Button>
            {onShowLeaderboard && (
              <button
                data-testid="dr-open-leaderboard"
                onClick={() => { onOpenChange(false); onShowLeaderboard(); }}
                className="w-full mt-2 py-2 text-xs text-purple-300 hover:text-purple-200 transition-colors flex items-center justify-center gap-1"
              >
                🏆 {t("dr_leaderboard_title")}
              </button>
            )}
            {onShowCustomAgents && user && (
              <button
                data-testid="dr-open-custom-agents"
                onClick={() => { onOpenChange(false); onShowCustomAgents(); }}
                className="w-full py-2 text-xs text-purple-300 hover:text-purple-200 transition-colors flex items-center justify-center gap-1"
              >
                🤖 {t("custom_agents_title")}
              </button>
            )}
          </div>
        )}

        {step === "domain" && (
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">{t("dr_pick_domain")}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-400 mt-1 mb-5">{t("dr_pick_domain_desc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {domains.map((d) => {
                const Icon = ICONS[d.icon] || Sparkles;
                return (
                  <button
                    key={d.id}
                    data-testid={`dr-domain-${d.id}`}
                    onClick={() => {
                      setDomain(d);
                      setStep("task");
                    }}
                    className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all text-left"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Icon size={18} className="text-purple-400" />
                      </div>
                      <span className="font-semibold text-white">{t(d.key)}</span>
                    </div>
                    <p className="text-xs text-zinc-500">{t(`${d.key}_desc`)}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === "task" && domain && (
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">{t(domain.key)}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-400 mt-1 mb-4">{t("dr_task_prompt")}</p>
            <Textarea
              data-testid="dr-task-input"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder={t("dr_task_placeholder")}
              className="min-h-[140px] bg-zinc-800 border-zinc-700 text-zinc-100 mb-4"
              maxLength={2000}
            />
            <div className="text-xs text-zinc-500 mb-4 flex justify-between">
              <span>{task.length}/2000</span>
              <span>{task.length < 20 ? t("dr_task_too_short") : t("dr_task_ok")}</span>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep("domain")}
                className="flex-1 border-zinc-700 text-zinc-300"
                data-testid="dr-back-btn"
              >
                {t("back")}
              </Button>
              <Button
                data-testid="dr-confirm-btn"
                disabled={task.length < 20 || loading}
                onClick={isAdmin ? startAdminJob : startPurchase}
                className="flex-1 h-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isAdmin ? (
                  <>{t("dr_start_free_admin")} <ArrowRight size={16} className="ml-1" /></>
                ) : (
                  <>{t("dr_pay_and_start").replace("{price}", `₺${price}`)} <ArrowRight size={16} className="ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-zinc-800/60 border border-zinc-700 p-3 text-center">
    <Icon size={18} className="text-purple-400 mx-auto mb-1" />
    <div className="text-lg font-bold text-white leading-tight">{value}</div>
    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{label}</div>
  </div>
);

export default DeepResearchModal;
